import simpleBarrier from 'simple-barrier';
import generateUuid from '../utils/generateUuid';

export const afterDoneTimeout = 1000;
// eslint-disable-next-line no-magic-numbers
export const forceEndTimeout = 90 * 1000;

export default function executorFactory(executor) {
	return {
		run: (notification, onDone) => {
			const executorsDestroyCallbacks = [];
			const partsWithoutExplicitDuration = {};
			// youtube executor waits for API script when first run, so instead of returning cleanup,
			// it registers listener for force end
			const endEvent = {
				listeners: [],
			};

			const onExecutorDone = () => {
				// we want to give some time for transitioning after ending the notification
				setTimeout(
					onDone,
					afterDoneTimeout
				);
			};

			const tryToForceEndAndCleanUp = () => {
				try {
					// close yt listeners
					endEvent.listeners.forEach(callback => {
						callback?.();
					});

					// close pending sound notifications
					Object.values(partsWithoutExplicitDuration).forEach(callback => {
						callback?.();
					});

					// call cleanup on standard notifications
					executorsDestroyCallbacks.forEach(c => {
						c?.();
					});
				} catch (cleanupError) {
					Screen.logErrorToSentry(cleanupError);
				}
			};

			let forceFullyEndTimeout;

			try {
				const notificationParts = notification.parts;
				const maxDuration = Math.max.apply(null, notificationParts.map(n => n.duration || 0));

				const setForceTimeOut = () => {
					forceFullyEndTimeout = setTimeout(() => {
						// eslint-disable-next-line max-len
						log(`Notification ${notification.id} (${notification?.parts?.map(p => p.notification_type)?.join(' + ')}) did not ended in time, forcefully ending`);
						tryToForceEndAndCleanUp();
						onExecutorDone();
					}, maxDuration + forceEndTimeout);
				};

				const prolongForceTimeOut = () => {
					// eslint-disable-next-line max-len
					log(`Notification ${notification.id} (${notification?.parts?.map(p => p.notification_type)?.join(' + ')}) prolonged its execution`);
					clearTimeout(forceFullyEndTimeout);
					setForceTimeOut();
				};

				// we need to make sure queue in unclogged if notification hangs too long for some reason
				setForceTimeOut();

				const barrier = simpleBarrier();

				barrier.endWith(() => {
					clearTimeout(forceFullyEndTimeout);

					// when standard notification parts end, we need to close every pending sound notification
					try {
						Object.values(partsWithoutExplicitDuration).forEach(callback => {
							callback?.();
						});
					} catch (destroyError) {
						Screen.logErrorToSentry(destroyError);
					}
					onExecutorDone();
				});

				for (const options of notificationParts) {
					// sound do not have explicit duration, so if they do not end in time other parts end, they need to be ended forcefully
					// except when they are the only part, in which case we allow it to play until they are done
					const otherThanSoundNotifications = notificationParts.filter(p => p.notification_type !== 'sound');
					if (options.notification_type === 'sound' && otherThanSoundNotifications.length > 0) {
						const optionId = options.id || generateUuid();
						const onSoundEndedPeacefully = () => {
							delete partsWithoutExplicitDuration[optionId];
						};

						// eslint-disable-next-line max-len
						const destroyCallback = executor(options, onSoundEndedPeacefully, { endEvent, prolong: prolongForceTimeOut });
						partsWithoutExplicitDuration[optionId] = destroyCallback;

					} else {
						// eslint-disable-next-line max-len
						const destroyCallback = executor(options, barrier.waitOn(), { endEvent, prolong: prolongForceTimeOut });
						executorsDestroyCallbacks.push(destroyCallback);
					}
				}
			
			}
			catch (e) {
				clearTimeout(forceFullyEndTimeout);
				// eslint-disable-next-line max-len
				log(`Notification ${notification.id} (${notification?.parts?.map(p => p.notification_type)?.join(' + ')}) error: ${e}`);
				Screen.logErrorToSentry(e);
				tryToForceEndAndCleanUp();

				onExecutorDone();
			}
		},
	};
}

